import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { useContent } from "@/content/ContentContext";

const EASE = [0.16, 1, 0.3, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#0A0A0C] border border-[rgba(212,175,55,0.22)] text-[#F7F7F8] text-sm px-4 py-3.5 placeholder:text-[#55555f] focus:outline-none focus:border-gold focus:ring-1 focus:ring-[rgba(212,175,55,0.4)] transition-colors duration-300";

export default function ContactSection() {
  const { content } = useContent();
  const c = content.contact;
  const interests = c.interests?.length ? c.interests : ["General Inquiry"];
  const [form, setForm] = useState({ fullName: "", email: "", phone: "", interest: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        full_name: form.fullName,
        email: form.email,
        phone: form.phone,
        interest: form.interest || interests[0],
        message: form.message,
      });
      toast.success("Message received. Sam's team will reach out to you directly.");
      setForm({ fullName: "", email: "", phone: "", interest: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please check your details and try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 sm:py-32 bg-[#0A0A0C] border-t border-[rgba(212,175,55,0.14)] overflow-hidden" data-testid="contact-section">
      <div aria-hidden="true" className="absolute right-[-12%] bottom-[-20%] w-[42vw] h-[42vw] gold-glow-radial pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative grid grid-cols-1 lg:grid-cols-12 gap-14">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="lg:col-span-5"
        >
          <p className="font-mono-lux text-xs uppercase tracking-[0.3em] text-gold mb-5" data-testid="contact-eyebrow">
            {c.eyebrow}
          </p>
          <h2 className="font-serif-lux text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-[#F7F7F8]" data-testid="contact-heading">
            {c.headingPre}<span className="italic text-gold-champagne">{c.headingAccent}</span>{c.headingPost}
          </h2>
          <p className="mt-6 text-base sm:text-lg text-[#B8B8C2] leading-relaxed max-w-md">
            {c.subtext}
          </p>
          <blockquote className="mt-10 border-l-2 border-gold pl-6">
            <p className="font-serif-lux italic text-xl text-gold-champagne leading-snug">
              “{c.quote}”
            </p>
            <cite className="mt-3 block font-mono-lux text-[11px] tracking-[0.25em] text-[#757582] not-italic">
              {c.quoteBy}
            </cite>
          </blockquote>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 36 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
          className="lg:col-span-7 bg-surface border border-[rgba(212,175,55,0.2)] p-8 sm:p-10"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label htmlFor="contact-name" className="block font-mono-lux text-[10px] uppercase tracking-[0.25em] text-gold mb-2.5">Full Name</label>
              <input id="contact-name" required value={form.fullName} onChange={set("fullName")} placeholder="e.g., Maria Santos" className={inputCls} data-testid="contact-input-name" />
            </div>
            <div>
              <label htmlFor="contact-email" className="block font-mono-lux text-[10px] uppercase tracking-[0.25em] text-gold mb-2.5">Email Address</label>
              <input id="contact-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} data-testid="contact-input-email" />
            </div>
            <div>
              <label htmlFor="contact-phone" className="block font-mono-lux text-[10px] uppercase tracking-[0.25em] text-gold mb-2.5">Phone / WhatsApp / Viber</label>
              <input id="contact-phone" value={form.phone} onChange={set("phone")} placeholder="+63 9XX XXX XXXX" className={inputCls} data-testid="contact-input-phone" />
            </div>
            <div>
              <label htmlFor="contact-interest" className="block font-mono-lux text-[10px] uppercase tracking-[0.25em] text-gold mb-2.5">Primary Focus</label>
              <select id="contact-interest" value={form.interest || interests[0]} onChange={set("interest")} className={inputCls} data-testid="contact-select-interest">
                {interests.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-6">
            <label htmlFor="contact-message" className="block font-mono-lux text-[10px] uppercase tracking-[0.25em] text-gold mb-2.5">Your Aspirations or Note</label>
            <textarea id="contact-message" required rows={5} value={form.message} onChange={set("message")} placeholder="Share a brief overview of your background and what you seek to achieve..." className={`${inputCls} resize-none`} data-testid="contact-textarea-message" />
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit-button"
            className="btn-shimmer mt-8 inline-flex items-center justify-center gap-2 w-full sm:w-auto bg-gold text-[#0A0A0B] font-semibold text-sm tracking-wide px-9 py-4 hover:bg-gold-champagne disabled:opacity-60 transition-colors duration-300 active:scale-[0.98]"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
            {sending ? "Sending..." : c.submit}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
